import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { RowClickEvent, SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	of,
	Subject,
	takeUntil,
	tap,
} from 'rxjs';

import { ComponentIcons } from '../../../../ui/app-components.icons';
import { DcIcons } from '../../../../ui/app-dc.icons';
import { DcLoaders } from '../../../../ui/app-loaders';
import { ColumnModelItemEntity } from '../../../domain/column-model-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../../domain/diagnostic-errors.entity';
import { ProfileItemEntity } from '../../../domain/profile-item.entity';
import { SubscriptionProvider } from '../../../providers/subscription.provider';
import {
	getProjects,
	refreshProjects,
	refreshSubscriptionProfiles,
	SubscriptionSelector,
} from '../../../store';
import { ProfilesComponent } from '../../profiles/profiles.component';
import {
	ActionConfirmPopupComponent,
	IActionConfirmationModalData,
} from '../action-confirm-popup/action-confirm-popup.component';
import { ConsumedProjectItemEntity } from '../consumed-project-item.entity';
import { ConsumerConfigViewProvider } from './consumer-config-view.provider';
import { ProjectListEntity } from './project-list.entity';
import { ProjectListProvider } from './project-list.provider';

export enum ConsumerConfigAction {
	Create = 'create',
	Update = 'update',
}

enum ConsumerConfigStep {
	ProfileSelection = 0,
	ProjectsSelection = 1,
}

@Component({
	selector: 'app-consumer-config-modal',
	templateUrl: './consumer-config-modal.component.html',
	styleUrls: ['./consumer-config-modal.component.scss'],
	providers: [
		ProjectListProvider,
		SubscriptionProvider,
		ConsumerConfigViewProvider,
	],
})
export class ConsumerConfigModalComponent<
		T extends {
			consumerConfig: ConsumedProjectItemEntity;
			action: ConsumerConfigAction;
		}
	>
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly ConsumerConfigStep = ConsumerConfigStep;
	protected readonly DcIcons = DcIcons;
	protected readonly SubscriptionErrorKey = SubscriptionErrorKey;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ConsumerConfigAction = ConsumerConfigAction;

	@ViewChild(ProfilesComponent)
	private readonly profilesCmp: ProfilesComponent | null = null;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	public vo$: Observable<{
		currentStep: ConsumerConfigStep;
		profiles: Map<string, ProfileItemEntity>;
		columnModels: Map<string, ColumnModelItemEntity>;
		selectedConsumer: ConsumedProjectItemEntity;
		projectList: ProjectListEntity;
		action: ConsumerConfigAction;
		errors: Map<SubscriptionErrorKey, SubscriptionDiagnosticError>;
	}>;

	private readonly currentStep$ = new BehaviorSubject<ConsumerConfigStep>(
		ConsumerConfigStep.ProfileSelection
	);

	private readonly consumers$ = new BehaviorSubject<Array<string>>(
		this.modalData.action === ConsumerConfigAction.Update
			? [this.modalData.consumerConfig.projectUuid]
			: []
	);
	private readonly isGridReadySubject$ = new Subject<boolean>();

	public constructor(
		private readonly selector: SubscriptionSelector,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly subscriptionProvider: SubscriptionProvider,
		private readonly projectListProvider: ProjectListProvider,
		private readonly consumerViewProvider: ConsumerConfigViewProvider,
		private readonly renderer: Renderer2,
		private readonly elemRef: ElementRef,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'consumer-config-modal';

		const projectList$ = this.selector
			.getProjects$()
			.pipe(
				map((availableProjects) =>
					this.projectListProvider.setListElements(availableProjects)
				)
			);

		this.vo$ = combineAll({
			currentStep: this.currentStep$,
			profiles: this.selector.getSubscriptionProfiles$(),
			columnModels: this.selector.getSubscriptionColumnModels$(),
			selectedConsumer: this.modalData.consumerConfig,
			projectList:
				this.modalData.action === ConsumerConfigAction.Create
					? projectList$
					: of(ProjectListEntity.build([])),
			action: this.modalData.action,
			errors: this.subscriptionProvider.errors$,
		});
	}

	public override ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
		this.handleSubscription();
	}

	public refreshProjects(): void {
		this.store.dispatch(refreshProjects());
	}

	public refreshProfiles(): void {
		this.store.dispatch(refreshSubscriptionProfiles());
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();

		if (this.modalData.action === ConsumerConfigAction.Create) {
			return;
		}

		setTimeout(
			() =>
				this.consumerViewProvider.adjustHeights(this.elemRef, this.renderer),
			750
		);
	}

	public setStep(step: ConsumerConfigStep): void {
		this.currentStep$.next(step);
	}

	public nextStep(): void {
		this.currentStep$.next(ConsumerConfigStep.ProjectsSelection);
	}

	public previousStep(): void {
		this.currentStep$.next(ConsumerConfigStep.ProfileSelection);
	}

	public selectProjects(event: SelectionChangedEvent): void {
		const projectUuids = event.selectedRowKeys as Array<string>;
		this.consumers$.next(projectUuids);
	}

	public async onRowClick(e: RowClickEvent): Promise<void> {
		const grid = e.component;

		if (grid.isRowSelected(e.key)) {
			await grid.deselectRows([e.key]);
		} else {
			await grid.selectRows([e.key], true);
		}
	}

	public onGridReady(): void {
		this.isGridReadySubject$.next(true);
	}

	public async createAction(): Promise<void> {
		if (!this.profilesCmp) {
			return;
		}
		const profiles = this.profilesCmp.getSelectedProfiles();

		if (profiles.length === 0) {
			return;
		}

		const [firstSelectedProfile] = profiles;

		const consumers = await firstValueFrom(this.consumers$);

		this.modalService.closeActiveModal({
			profileUuid: firstSelectedProfile.id,
			consumerIds: consumers,
		});
	}

	public async updateAction(): Promise<void> {
		if (!this.profilesCmp || !this.modalData.consumerConfig) {
			return;
		}

		const profiles = this.profilesCmp.getSelectedProfiles();

		if (profiles.length === 0) {
			return;
		}

		const [firstSelectedProfile] = profiles;

		if (this.modalData.consumerConfig.profileUuid === firstSelectedProfile.id) {
			return this.modalService.closeActiveModal({
				profileUuid: firstSelectedProfile.id,
			});
		}

		const modalConfirm =
			this.modalService.openPopup<IActionConfirmationModalData>(
				ActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.consumedProject.action.confirmation.popup.update.header:`,
					subTitle: '',
					id: this.modalData.consumerConfig.id,
					element: this.modalData.consumerConfig.projectLabel,
					action: 'UPDATE_CONSUMER',
				},
				{
					disableClose: true,
				}
			);

		const modalConfirmResult = await firstValueFrom(modalConfirm.afterClosed());

		if (!modalConfirmResult) {
			return;
		}

		this.modalService.closeActiveModal({
			profileUuid: firstSelectedProfile.id,
		});
	}

	private triggerInit(): void {
		if (this.modalData.action === ConsumerConfigAction.Update) {
			return;
		}

		this.store.dispatch(getProjects());
	}

	private handleSubscription(): void {
		this.consumers$
			.pipe(
				takeUntil(this.onDestroy$),
				tap((consumers) => {
					if (consumers.length === 0) {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.noSelectProject:`,
							SubscriptionErrorKey.NoSelectProject
						);
					} else {
						this.subscriptionProvider.removeError(
							SubscriptionErrorKey.NoSelectProject
						);
					}
				})
			)
			.subscribe();

		this.isGridReadySubject$
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(300),
				tap(() => {
					this.grid?.instance.updateDimensions();
				})
			)
			.subscribe();
	}
}
